/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {Tooltip} from 'react-tooltip'
import {SparklesIcon} from '@heroicons/react/24/outline'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import UiCounter from 'components/Ui/UiCounter'
import UiDatePicker from 'components/Ui/UiDatePicker'
import UiButton, {UiButtonSize, UiButtonStyle, UiButtonType} from 'components/Ui/UiButton'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import LeadTagsAutocomplete from './LeadTagsAutocomplete'
import LeadSourceSelect from './LeadSourceSelect'
import AnlasstypSelect from './AnlasstypSelect'
import UiSellerSelect from 'components/leads/UiSellerSelect'
import {DATE_FORMATS} from 'constants/DateFormats'
import {LeadBasicInputs} from 'components/leads/schemas/useLeadBasicSchema'
import {LeadStatus} from 'types/enums/LeadStatus'
import {Lead} from 'types/entities/Lead'
import {isClosingDateEditableStatus} from 'utils/leads/opportunityDateUtils'
import {
    formatParticipantCount,
    getEffectiveParticipants,
    getReservedParticipants,
} from 'utils/leads/participantCounts'

interface LeadBasicFormLeadInfoSectionProps {
    isOpportunity: boolean
    opportunityStatus?: LeadStatus
    lead?: Lead | null
    compact: boolean
    testField: (field: string) => boolean
    tags: string[]
    setTags: (tags: string[]) => void
    onCreateOpportunityName?: () => void
}

const LeadBasicFormLeadInfoSection: React.FC<LeadBasicFormLeadInfoSectionProps> = ({
    isOpportunity,
    opportunityStatus,
    lead,
    compact,
    testField,
    setTags,
    onCreateOpportunityName,
}) => {
    const {t} = useTranslation()
    const {register, control, formState: {errors}, watch} = useFormContext<LeadBasicInputs>()
    const isClosingDateEditable = isClosingDateEditableStatus(opportunityStatus)
    const anlasstypValue = watch('anlasstyp')

    const leadNameLabel = t(isOpportunity ? 'Opportunity Name' : 'Lead Name')
    const leadNamePlaceholder = t(isOpportunity ? 'Enter opportunity name' : 'Enter lead name')

    return (
        <UiFormInner
            title={isOpportunity ? t('Opportunity Information') : t('Lead Information')}
            className={clsx('LeadBasicFormLeadInfoSection h-full lg:order-1')}
        >
            <UiFormGrid cols={2} gap={compact ? 3 : 3} responsive={false} className={clsx('grid-cols-2')}>
                <Controller
                    control={control}
                    name="anlasstyp"
                    render={({field}) => (
                        <AnlasstypSelect
                            className={clsx('col-span-1')}
                            value={field.value ?? null}
                            onChange={(val) => field.onChange(val)}
                            error={errors.anlasstyp?.message}
                            errors={errors}
                        />
                    )}
                />

                <div className={clsx('col-span-1', isOpportunity && 'flex items-end gap-2')}>
                    <CompanyFormInput
                        className={clsx(isOpportunity ? 'min-w-0 flex-1' : 'col-span-1')}
                        label={leadNameLabel}
                        name="name"
                        register={register}
                        errors={errors}
                        placeholder={leadNamePlaceholder}
                        required={testField('name')}
                        autoComplete="one-time-code"
                    />
                    {isOpportunity && onCreateOpportunityName && (
                        <>
                            <span
                                className={clsx('mb-0 inline-flex h-10 w-10 shrink-0')}
                                data-tooltip-id="create-oppo-name-tooltip"
                                data-tooltip-content={
                                    anlasstypValue?.trim()
                                        ? t('create Oppo Name')
                                        : t('Select Anlasstyp first')
                                }
                            >
                                <UiButton
                                    type={UiButtonType.Button}
                                    style={UiButtonStyle.Secondary}
                                    size={UiButtonSize.Small}
                                    className={clsx('h-10 w-10 p-0!')}
                                    disabled={!anlasstypValue?.trim()}
                                    onClick={onCreateOpportunityName}
                                    icon={<SparklesIcon className={clsx('h-5 w-5')} />}
                                />
                            </span>
                            <Tooltip
                                id="create-oppo-name-tooltip"
                                place="top"
                                offset={28}
                                style={{zIndex: 99999}}
                            />
                        </>
                    )}
                </div>

                <UiFormField className={clsx('col-span-1')} label={t('Seller')}>
                    <Controller
                        control={control}
                        name="seller_id"
                        render={({field}) => (
                            <UiSellerSelect selected={field.value ?? null} setSelected={(val) => field.onChange(val)} />
                        )}
                    />
                </UiFormField>

                <UiFormField
                    className={clsx('col-span-1')}
                    label={t(isOpportunity ? 'Requested participants' : 'Persons')}
                    error={errors.persons?.message}
                >
                    <Controller
                        control={control}
                        name="persons"
                        render={({field}) => (
                            <UiCounter value={field.value} onChange={field.onChange} min={0} />
                        )}
                    />
                </UiFormField>

                {isOpportunity && (
                    <>
                        <UiFormField className={clsx('col-span-1')} label={t('Reserved participants')}>
                            <div className="flex h-10 items-center text-sm text-gray-700">
                                {formatParticipantCount(getReservedParticipants(lead))}
                            </div>
                        </UiFormField>

                        <UiFormField className={clsx('col-span-1')} label={t('Effective participants')}>
                            <div className="flex h-10 items-center text-sm text-gray-700">
                                {formatParticipantCount(getEffectiveParticipants(lead))}
                            </div>
                        </UiFormField>
                    </>
                )}

                <UiFormField
                    className={clsx('col-span-1')}
                    label={t('Event date')}
                    error={errors.requested_date?.message}
                >
                    <Controller
                        control={control}
                        name="requested_date"
                        render={({field}) => (
                            <UiDatePicker
                                value={(field.value ? new Date(field.value) : null) as Date}
                                includeTime={true}
                                format={DATE_FORMATS.DATE_TIME_DAY}
                                onChange={(date) => field.onChange(date ?? null)}
                            />
                        )}
                    />
                </UiFormField>

                {isOpportunity && (
                    <>
                        <UiFormField
                            className={clsx('col-span-1')}
                            label={t('Creation Date')}
                            error={errors.created_date?.message}
                        >
                            <Controller
                                control={control}
                                name="created_date"
                                render={({field}) => (
                                    <UiDatePicker
                                        value={(field.value ? new Date(field.value) : null) as Date}
                                        includeTime={false}
                                        format={DATE_FORMATS.DATE}
                                        onChange={(date) => field.onChange(date ?? null)}
                                    />
                                )}
                            />
                        </UiFormField>

                        <UiFormField
                            className={clsx('col-span-1')}
                            label={t('Closing Date')}
                            error={errors.closed_at?.message}
                        >
                            <Controller
                                control={control}
                                name="closed_at"
                                render={({field}) => (
                                    <UiDatePicker
                                        value={
                                            isClosingDateEditable && field.value
                                                ? (new Date(field.value) as Date)
                                                : null
                                        }
                                        includeTime={false}
                                        format={DATE_FORMATS.DATE}
                                        disabled={!isClosingDateEditable}
                                        onChange={(date) => field.onChange(date ?? null)}
                                    />
                                )}
                            />
                        </UiFormField>
                    </>
                )}

                <Controller
                    control={control}
                    name="source"
                    render={({field}) => (
                        <LeadSourceSelect
                            className={clsx('col-span-1')}
                            label={isOpportunity ? t('Opportunity Herkunft') : t('Lead Herkunft')}
                            value={field.value}
                            onChange={(val) => field.onChange(val)}
                            error={errors.source?.message}
                        />
                    )}
                />

                <UiFormField
                    className={clsx('col-span-1')}
                    label={t('Tags')}
                    error={
                        errors.tags
                            ? Array.isArray(errors.tags)
                                ? (errors.tags as {message?: string}[])[0]?.message
                                : errors.tags.message
                            : undefined
                    }
                >
                    <Controller
                        control={control}
                        name="tags"
                        render={({field}) => (
                            <LeadTagsAutocomplete
                                selectedTags={field.value || []}
                                onTagsChange={(newTags) => {
                                    field.onChange(newTags)
                                    setTags(newTags)
                                }}
                                placeholder={t('Type to search or add tags...')}
                            />
                        )}
                    />
                </UiFormField>
            </UiFormGrid>
        </UiFormInner>
    )
}

export default LeadBasicFormLeadInfoSection
